import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FlexControlledForm,
  PhoneVerificationIcon,
  useDebounceState,
  useResponsive,
} from '@common-fe/common-fe';

import REGEXP from '@/common/regexp';
import ResendCodeButton from '@/modules/user/components/Verification/components/ResendCodeButton';
import VerificationCodeModal from '@/modules/user/components/Verification/components/VerificationCodeModal';
import useVerificationCodeFields, {
  FormValues,
} from '@/modules/user/components/Verification/hooks/useVerificationCodeFields';
import useSendVerificationCodeQuery from '@/modules/user/components/Verification/queries/useSendVerificationCode.query';
import useValidateVerifyCodeQuery from '@/modules/user/components/Verification/queries/useValidateVerifyCode.query';
import {
  VerificationErrorText,
  VerificationType,
} from '@/modules/user/components/Verification/verification.types';
import useUpdateEmployeeQuery from '@/modules/user/queries/useUpdateEmployee.query';
import { useValidateForm } from '@/utils/hooks';

interface Props {
  phoneNumber: string;
  onSetVisible: (value: boolean) => void;
  onBack?: () => void;
  onNext: () => void;
}

const PhoneVerificationCodeModal: React.FC<Props> = ({ phoneNumber, onSetVisible, onBack, onNext }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const [verificationErrorText, setVerificationErrorText] = useState('');
  const fields = useVerificationCodeFields({ verificationErrorText });
  const { updateEmployee } = useUpdateEmployeeQuery();
  const [state, handleSetState] = useState<FormValues>();
  const { values, setValues } = useDebounceState<FormValues>();
  const { handlePreSubmit, hasErrors } = useValidateForm(fields);
  const { validateVerifyCode, isLoading } = useValidateVerifyCodeQuery();
  const { sendVerificationCode } = useSendVerificationCodeQuery();

  const handleConfirm = useCallback(async () => {
    try {
      const isSuccess = handlePreSubmit(state);
      if (isSuccess) {
        setVerificationErrorText('');
        const response = await validateVerifyCode({
          code: state?.verificationCode?.replace(REGEXP.SPACE_SYMBOL, '') || '',
          value: phoneNumber,
          type: VerificationType.SMS,
        });
        if (!response?.verified) {
          throw new Error();
        }
        updateEmployee({ is_sms_notification_enabled: true });
        onNext();
      }
    } catch (e) {
      setVerificationErrorText(VerificationErrorText.CODE_IS_INVALID);
    }
  }, [state, phoneNumber, onNext, validateVerifyCode, handlePreSubmit, updateEmployee]);

  const handleResendCode = useCallback(async () => {
    await sendVerificationCode({
      type: VerificationType.SMS,
      value: phoneNumber,
    });
  }, [sendVerificationCode, phoneNumber]);

  useEffect(() => {
    if (values) {
      handleSetState(values);
      setVerificationErrorText('');
    }
  }, [handleSetState, values]);

  return (
    <VerificationCodeModal
      testId="phone-verification-code"
      icon={<PhoneVerificationIcon size="128px" />}
      title={t('Phone Verification')}
      value={phoneNumber}
      isLoading={isLoading}
      onSetVisible={onSetVisible}
      onBack={onBack}
      onConfirm={handleConfirm}
      form={
        <FlexControlledForm
          fields={fields}
          onChangeValues={setValues}
          formStyle={{ margin: '24px' }}
          showError={hasErrors || !!verificationErrorText}
          editMode
        >
          <Box width="320px" alignSelf={isMobile ? 'start' : 'end'}>
            <ResendCodeButton onResend={handleResendCode} />
          </Box>
        </FlexControlledForm>
      }
    />
  );
};

export default PhoneVerificationCodeModal;
